import { Icon } from "react-migration/components/Icon";
import { Section } from "react-migration/components/Section";
import { Dispatch, SetStateAction } from "react";
import { DesignationHatch } from "react-migration/domains/constraints/components/Hatch";
import { ListSearchInput } from "react-migration/domains/constraints/components/ListSearchInput";
import { SingleDesignation } from "react-migration/domains/constraints/typings/applicationTypes/SingleDesignation";
import { SourceStatus } from "../SourceStatus";
import { sortDesignationsByName, sortDesignationsBySubCategory } from "./sortDesignations";
import { getDesignationFillColour, getDesignationLineColour } from "../../style/accessors";
import { DesignationSubTitle, DesignationTitle } from "react-migration/layouts/designations/Designation";
import { logEventV2, WorkbenchEventName } from "react-migration/lib/util/logEventV2";
import { useLayerContext } from "react-migration/layouts/map/Multilayer/Bundle/Workbench/AssessmentTab/LayerContext";
import { ConstraintsLayer } from "react-migration/layouts/map/Multilayer/layer_types/ConstraintsLayerType";

export interface DesignationsProps {
  filterable: boolean;
  designations: SingleDesignation[];
  setSelectedDesignation: Dispatch<SingleDesignation["id"]>;
  filterString: string;
  setFilterString: Dispatch<SetStateAction<string>>;
  truncateSubtitle: boolean;
  cardName: string;
}

export function Designations({
  filterable,
  designations,
  setSelectedDesignation,
  setFilterString,
  filterString,
  truncateSubtitle,
  cardName,
}: DesignationsProps) {
  const designationList = [...designations].sort(sortDesignationsByName).sort(sortDesignationsBySubCategory);

  const { layer } = useLayerContext<ConstraintsLayer>();
  const SubTitleComponent = layer?.layerConfig?.components?.DesignationList?.SubTitle ?? DesignationSubTitle;

  return (
    <div className="space-y-3 rounded-sm shrink-0" data-testid="designations-body">
      <Section bodyClasses="flex flex-col text-sm rounded-sm">
        {filterable && <ListSearchInput value={filterString} onChange={setFilterString} />}
        <div className="p-2 pt-0">
          <div className="overflow-hidden bg-white border divide-y rounded-sm border-border-action divide-border-base-divider drop-shadow-xs empty:hidden">
            {designationList.map((designation) => {
              return (
                <button
                  key={designation.id}
                  className="flex items-center w-full overflow-hidden text-left cursor-pointer hover:bg-neutral-100"
                  onClick={() => {
                    setSelectedDesignation(designation.id);
                    logEventV2({
                      name: WorkbenchEventName.WORKBENCH_CARD_CLICK,
                      properties: {
                        cardName: cardName,
                        action: "goTo",
                        clickDetail: designation.display_name as string,
                      },
                    });
                  }}
                  // A global `border-style: none` is applied to buttons. This
                  // is to show divider lines between designation items.
                  style={{ borderStyle: "inherit" }}
                >
                  <div className="flex flex-col min-w-0 gap-2 p-2 grow">
                    <div className="flex items-start">
                      <div className="mr-2">
                        <DesignationHatch
                          id={designation.sub_category_id}
                          fillColorOverride={getDesignationFillColour(designation)}
                          lineColorOverride={getDesignationLineColour(designation)}
                        />
                      </div>
                      <div className="flex flex-col min-w-0 gap-1 grow">
                        <div className="flex items-start justify-between">
                          <div className="font-bold text-left text-text-base-active focus:text-text-base-active-hover hover:text-text-base-active-hover">
                            <DesignationTitle designation={designation} />
                          </div>

                          <SourceStatus status={designation.source.status} />
                        </div>

                        <SubTitleComponent designation={designation} truncate={truncateSubtitle} />
                      </div>
                    </div>
                  </div>
                  <div className="shrink-0 text-neutral-500">
                    <Icon icon="icon-lt-arrow-right-s-line" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </Section>
    </div>
  );
}
